import React, { useState, useEffect, useCallback } from 'react';
import { breachAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ShieldAlert, Plus, Clock, CheckCircle2, AlertTriangle, XCircle, Send, FileText, ChevronRight, Timer } from 'lucide-react';
import { cn } from '../../lib/utils';

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-600 text-white', label: 'Critical' },
  high: { color: 'bg-orange-500 text-white', label: 'High' },
  medium: { color: 'bg-amber-500 text-white', label: 'Medium' },
  low: { color: 'bg-blue-500 text-white', label: 'Low' },
};

const STATUS_CONFIG = {
  investigating: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertTriangle },
  contained: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: ShieldAlert },
  resolved: { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle2 },
  notified: { color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400', icon: Send },
};

function UrgencyIndicator({ hoursRemaining, isOverdue, notificationSent }) {
  if (notificationSent) {
    return <Badge variant="outline" className="text-emerald-600 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Notified</Badge>;
  }
  if (isOverdue) {
    return <Badge variant="destructive" className="animate-pulse"><XCircle className="h-3 w-3 mr-1" />OVERDUE</Badge>;
  }
  if (hoursRemaining < 24) {
    return <Badge className="bg-red-500 text-white"><Timer className="h-3 w-3 mr-1" />{hoursRemaining}h left</Badge>;
  }
  if (hoursRemaining < 48) {
    return <Badge className="bg-amber-500 text-white"><Clock className="h-3 w-3 mr-1" />{hoursRemaining}h left</Badge>;
  }
  return <Badge variant="outline" className="text-muted-foreground"><Clock className="h-3 w-3 mr-1" />{hoursRemaining}h left</Badge>;
}

function BreachCard({ breach, onSelect }) {
  const severity = SEVERITY_CONFIG[breach.severity] || SEVERITY_CONFIG.medium;
  const status = STATUS_CONFIG[breach.status] || STATUS_CONFIG.investigating;
  const StatusIcon = status.icon;

  return (
    <Card
      className="cursor-pointer hover:border-primary/40 transition-colors"
      onClick={() => onSelect(breach)}
      data-testid={`breach-card-${breach.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge className={cn("text-[10px] px-1.5 py-0", severity.color)}>{severity.label}</Badge>
              <Badge className={cn("text-[10px] px-1.5 py-0", status.color)}>
                <StatusIcon className="h-2.5 w-2.5 mr-0.5" />{breach.status}
              </Badge>
            </div>
            <h4 className="text-sm font-semibold truncate">{breach.title}</h4>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{breach.description}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span>{new Date(breach.reported_at).toLocaleDateString()}</span>
              {breach.estimated_affected_users > 0 && (
                <span>{breach.estimated_affected_users} users affected</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <UrgencyIndicator
              hoursRemaining={breach.hours_remaining}
              isOverdue={breach.is_overdue}
              notificationSent={breach.notification_sent}
            />
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreachDetail({ breach, onBack, onRefresh }) {
  const [updateStatus, setUpdateStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [remediation, setRemediation] = useState('');
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const severity = SEVERITY_CONFIG[breach.severity] || SEVERITY_CONFIG.medium;

  const handleUpdate = async () => {
    if (!updateStatus && !notes && !remediation) return;
    setSaving(true);
    try {
      await breachAPI.update(breach.id, {
        status: updateStatus || undefined,
        notes: notes || undefined,
        remediation_steps: remediation || undefined,
      });
      toast.success('Breach updated');
      setNotes('');
      setRemediation('');
      setUpdateStatus('');
      onRefresh();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleNotify = async () => {
    setNotifying(true);
    try {
      const res = await breachAPI.markNotified(breach.id);
      toast.success(res.data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send notification');
    } finally { setNotifying(false); }
  };

  return (
    <div className="space-y-6" data-testid="breach-detail">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="breach-back-btn">Back</Button>
        <Separator orientation="vertical" className="h-5" />
        <Badge className={cn("text-xs", severity.color)}>{severity.label}</Badge>
        <h3 className="text-lg font-bold truncate">{breach.title}</h3>
      </div>

      {/* Urgency banner */}
      {!breach.notification_sent && (
        <div className={cn(
          "rounded-lg border p-4 flex items-center justify-between",
          breach.is_overdue
            ? "border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800"
            : breach.hours_remaining < 24
              ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800"
              : "border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800"
        )}>
          <div>
            <p className={cn("text-sm font-semibold", breach.is_overdue ? "text-red-700 dark:text-red-400" : "text-foreground")}>
              {breach.is_overdue ? 'GDPR 72-Hour Deadline EXCEEDED' : `${breach.hours_remaining} hours remaining to notify authority`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              GDPR Article 33 requires notification within 72 hours of becoming aware of a breach.
            </p>
          </div>
          <Button
            size="sm"
            variant={breach.is_overdue ? "destructive" : "default"}
            onClick={handleNotify}
            disabled={notifying}
            data-testid="notify-authority-btn"
          >
            {notifying ? <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="h-3.5 w-3.5 mr-2" />}
            Mark Authority Notified
          </Button>
        </div>
      )}

      {breach.notification_sent && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
              Authority notified {breach.notification_on_time ? '(within 72hrs)' : '(after deadline)'}
            </p>
          </div>
          <p className="text-xs text-muted-foreground ml-6 mt-0.5">
            Notified on {new Date(breach.notification_sent_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* Details grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Reported', value: new Date(breach.reported_at).toLocaleString() },
          { label: 'Deadline', value: new Date(breach.notification_deadline).toLocaleString() },
          { label: 'Users Affected', value: breach.estimated_affected_users || 'Unknown' },
          { label: 'Status', value: breach.status },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-md border p-3">
            <p className="text-[11px] text-muted-foreground">{label}</p>
            <p className="text-sm font-semibold mt-0.5">{value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-md border p-4">
        <p className="text-xs font-semibold text-muted-foreground mb-1">Description</p>
        <p className="text-sm">{breach.description}</p>
        {breach.affected_data_types?.length > 0 && (
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {breach.affected_data_types.map(t => (
              <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
            ))}
          </div>
        )}
      </div>

      {breach.remediation_steps && (
        <div className="rounded-md border p-4">
          <p className="text-xs font-semibold text-muted-foreground mb-1">Remediation Steps</p>
          <p className="text-sm">{breach.remediation_steps}</p>
        </div>
      )}

      {/* Notes timeline */}
      {breach.notes?.length > 0 && (
        <div className="rounded-md border p-4">
          <p className="text-xs font-semibold text-muted-foreground mb-3">Activity Log</p>
          <div className="space-y-2">
            {breach.notes.map((note, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <span className="text-xs text-muted-foreground shrink-0">{new Date(note.at).toLocaleString()}</span>
                <span>{note.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Separator />

      {/* Update form */}
      <div className="space-y-3">
        <p className="text-sm font-semibold">Update Breach</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={updateStatus} onValueChange={setUpdateStatus}>
            <SelectTrigger data-testid="breach-status-select"><SelectValue placeholder="Change status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="contained">Contained</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Add a note..." value={notes} onChange={e => setNotes(e.target.value)} data-testid="breach-note-input" />
          <Input placeholder="Remediation steps..." value={remediation} onChange={e => setRemediation(e.target.value)} data-testid="breach-remediation-input" />
        </div>
        <Button size="sm" disabled={saving || (!updateStatus && !notes && !remediation)} onClick={handleUpdate} data-testid="breach-update-btn">
          {saving ? 'Saving...' : 'Update Breach'}
        </Button>
      </div>
    </div>
  );
}

export default function BreachManagement() {
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportForm, setReportForm] = useState({ title: '', description: '', severity: 'high', affected_data_types: '', estimated_affected_users: 0 });
  const [reporting, setReporting] = useState(false);

  const fetchBreaches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await breachAPI.list();
      setBreaches(res.data.breaches || []);
    } catch { toast.error('Failed to load breaches'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBreaches(); }, [fetchBreaches]);

  const handleRefresh = async () => {
    await fetchBreaches();
    if (selected) {
      try {
        const res = await breachAPI.get(selected.id);
        setSelected(res.data);
      } catch { setSelected(null); }
    }
  };

  const handleReport = async () => {
    if (!reportForm.title || !reportForm.description) { toast.error('Title and description required'); return; }
    setReporting(true);
    try {
      const payload = {
        ...reportForm,
        affected_data_types: reportForm.affected_data_types ? reportForm.affected_data_types.split(',').map(s => s.trim()) : [],
        estimated_affected_users: parseInt(reportForm.estimated_affected_users) || 0,
      };
      const res = await breachAPI.report(payload);
      toast.success(res.data.message);
      setShowReport(false);
      setReportForm({ title: '', description: '', severity: 'high', affected_data_types: '', estimated_affected_users: 0 });
      await fetchBreaches();
    } catch { toast.error('Failed to report breach'); }
    finally { setReporting(false); }
  };

  if (selected) {
    return <BreachDetail breach={selected} onBack={() => setSelected(null)} onRefresh={handleRefresh} />;
  }

  const overdue = breaches.filter(b => b.is_overdue);
  const active = breaches.filter(b => !b.notification_sent && !b.is_overdue);
  const resolved = breaches.filter(b => b.notification_sent);

  return (
    <div className="space-y-6" data-testid="breach-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-500" /> Data Breach Management
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">GDPR Article 33 — 72-hour notification compliance</p>
        </div>
        <Button onClick={() => setShowReport(true)} data-testid="report-breach-btn">
          <Plus className="h-4 w-4 mr-2" /> Report Breach
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : breaches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldAlert className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">No breach incidents reported</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1.5">
                <XCircle className="h-4 w-4" /> Overdue ({overdue.length})
              </h3>
              <div className="space-y-2">
                {overdue.map(b => <BreachCard key={b.id} breach={b} onSelect={setSelected} />)}
              </div>
            </div>
          )}
          {active.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-amber-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> Active ({active.length})
              </h3>
              <div className="space-y-2">
                {active.map(b => <BreachCard key={b.id} breach={b} onSelect={setSelected} />)}
              </div>
            </div>
          )}
          {resolved.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Notified ({resolved.length})
              </h3>
              <div className="space-y-2">
                {resolved.map(b => <BreachCard key={b.id} breach={b} onSelect={setSelected} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Report Breach Dialog */}
      <AlertDialog open={showReport} onOpenChange={setShowReport}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" data-testid="report-breach-dialog-title">
              <ShieldAlert className="h-5 w-5 text-red-500" /> Report Data Breach
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm">This starts the 72-hour GDPR notification clock. All details are logged immutably.</p>
                <div className="space-y-2">
                  <Input placeholder="Breach title" value={reportForm.title} onChange={e => setReportForm(p => ({ ...p, title: e.target.value }))} data-testid="breach-title-input" />
                  <textarea
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-none"
                    placeholder="Describe what happened..."
                    value={reportForm.description}
                    onChange={e => setReportForm(p => ({ ...p, description: e.target.value }))}
                    data-testid="breach-description-input"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={reportForm.severity} onValueChange={v => setReportForm(p => ({ ...p, severity: v }))}>
                      <SelectTrigger data-testid="breach-severity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" placeholder="Affected users" value={reportForm.estimated_affected_users} onChange={e => setReportForm(p => ({ ...p, estimated_affected_users: e.target.value }))} data-testid="breach-users-input" />
                  </div>
                  <Input placeholder="Affected data types (comma-separated)" value={reportForm.affected_data_types} onChange={e => setReportForm(p => ({ ...p, affected_data_types: e.target.value }))} data-testid="breach-data-types-input" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={reporting || !reportForm.title} onClick={handleReport} data-testid="breach-submit-btn">
              {reporting ? 'Reporting...' : 'Report Breach'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
